/**
 * AI Context Provider
 * 
 * Manages chat state, page context, and message history for the AI assistant.
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { useLocation } from 'react-router-dom';
import { findV2AccessItemForPath } from '../routes/routeMetaV2';
import { useAuth } from './AuthContext';

const AIContext = createContext(null);

export const useAIContext = () => {
  const context = useContext(AIContext);
  if (!context) {
    throw new Error('useAIContext must be used within AIContextProvider');
  }
  return context;
};

export const AIContextProvider = ({ children }) => {
  const location = useLocation();
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(true); // AI assistant visible by default (starts minimized)
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [pageContext, setPageContext] = useState('dashboard');
  // UI signals used by other parts of the app to interact with the assistant widget.
  const [assistantHintOpen, setAssistantHintOpen] = useState(false);
  const [expandRequestId, setExpandRequestId] = useState(0);
  // Hide FAB on specific pages (e.g., FRAT has its own Copilot)
  const [hideFAB, setHideFAB] = useState(false);
  // Signal to show AI Assistant icon in FRAT page
  const [showAIInFRAT, setShowAIInFRAT] = useState(false);
  const skipPersistenceKeyRef = useRef(null);
  const messageStorageKey = useMemo(() => {
    const userId = user?.id || user?.pk;
    if (!userId) return null;
    const tenantId = user?.company_id
      || user?.company?.id
      || user?.tenant_schema;
    if (!tenantId) return null;
    return `ai_assistant_messages:${tenantId}:${userId}`;
  }, [user?.company?.id, user?.company_id, user?.id, user?.pk, user?.tenant_schema]);

  // Get page context from current route
  useEffect(() => {
    const path = location.pathname;
    let context = 'dashboard';
    const routeItem = findV2AccessItemForPath(path);

    if (routeItem?.id) {
      // Curated route metadata is authoritative. The substring buckets below
      // only cover legacy/unmapped routes.
      context = routeItem.id;
    } else if (path.includes('/risk-assessment/frat') || path.includes('/risk-assessment/risk-matrix') || path.includes('/safety')) {
      context = 'safety-mode';
    } else if (path.includes('/fuel-analyzer') || path.includes('/unified-fuel-entry')) {
      context = 'fuel-analyzer';
    } else if (path.includes('/invoice') || path.includes('/costs') || path.includes('prestacao')) {
      context = 'invoice-wizard';
    } else if (path.includes('/flight-logs')) {
      context = 'flight-logs';
    } else if (path.includes('/aircraft')) {
      context = 'aircraft';
    } else {
      context = 'dashboard';
    }

    setPageContext(context);
  }, [location.pathname]);

  // Load user/tenant-scoped history for this browser tab only.
  useEffect(() => {
    skipPersistenceKeyRef.current = messageStorageKey;
    setMessages([]);
    if (!messageStorageKey) return;
    try {
      // Remove the old unscoped key so one account can never inherit another
      // account's assistant conversation after logout/login.
      localStorage.removeItem('ai_assistant_messages');
      const savedMessages = sessionStorage.getItem(messageStorageKey);
      if (savedMessages) {
        const parsed = JSON.parse(savedMessages);
        // Only load messages from last 24 hours
        const oneDayAgo = Date.now() - 24 * 60 * 60 * 1000;
        const filtered = parsed.filter((msg) => {
          const msgTime = new Date(msg.timestamp).getTime();
          return msgTime > oneDayAgo;
        });
        setMessages(filtered);
      }
    } catch (error) {
      console.error('Error loading chat history:', error);
    }
  }, [messageStorageKey]);

  // Persist only within the scoped session.
  useEffect(() => {
    if (!messageStorageKey) return;
    if (skipPersistenceKeyRef.current === messageStorageKey) {
      skipPersistenceKeyRef.current = null;
      return;
    }
    try {
      if (messages.length > 0) {
        sessionStorage.setItem(messageStorageKey, JSON.stringify(messages));
      } else {
        sessionStorage.removeItem(messageStorageKey);
      }
    } catch (error) {
      console.error('Error saving chat history:', error);
    }
  }, [messageStorageKey, messages]);

  const addMessage = useCallback((message) => {
    setMessages((prev) => [...prev, message]);
  }, []);

  const clearMessages = useCallback(() => {
    setMessages([]);
    if (messageStorageKey) sessionStorage.removeItem(messageStorageKey);
  }, [messageStorageKey]);

  const toggleChat = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const openChat = useCallback(() => {
    setIsOpen(true);
  }, []);

  const closeChat = useCallback(() => {
    setIsOpen(false);
  }, []);

  // Force the assistant to expand (un-minimize) if it's currently minimized.
  // This is modeled as an incrementing id so listeners can react to repeated requests.
  const requestChatExpand = useCallback(() => {
    setExpandRequestId((prev) => prev + 1);
  }, []);

  const openAndExpandChat = useCallback(() => {
    setIsOpen(true);
    setExpandRequestId((prev) => prev + 1);
  }, []);

  const setAssistantHintVisible = useCallback((visible) => {
    setAssistantHintOpen(Boolean(visible));
  }, []);

  const value = {
    isOpen,
    messages,
    isLoading,
    pageContext,
    assistantHintOpen,
    expandRequestId,
    hideFAB,
    showAIInFRAT,
    setIsLoading,
    addMessage,
    clearMessages,
    toggleChat,
    openChat,
    openAndExpandChat,
    closeChat,
    requestChatExpand,
    setAssistantHintVisible,
    setHideFAB,
    setShowAIInFRAT,
  };

  return <AIContext.Provider value={value}>{children}</AIContext.Provider>;
};

